'use client';

import React, { useEffect, useReducer, useState } from 'react';
import { useForm } from 'react-hook-form';

import { createPartners } from '@/actions/workspace';
import { workspaceModalSteps } from '@/config/contents';
import { partnerDomainSchema } from '@/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { usePathname } from 'next/navigation';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Form,
    FormControl,
    FormDescription,
    FormItem,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { activeChannelAtom } from '../atoms';
import { useCreatePartnerModal } from '../store/use-create-workspace-modal';
import { useChannel } from './use-channels';

type PartnerAction =
    | { type: 'ADD_DOMAIN'; domain: string }
    | { type: 'DELETE_DOMAIN'; domain: string }
    | { type: 'SET_DOMAINS'; domains: string[] }
    | { type: 'RESET' };

function partnerDomainsReducer(
    state: string[],
    action: PartnerAction
): string[] {
    switch (action.type) {
        case 'ADD_DOMAIN':
            return [...state, action.domain];
        case 'DELETE_DOMAIN':
            return state.filter((domain) => domain !== action.domain);
        case 'SET_DOMAINS':
            return action.domains;
        case 'RESET':
            return [];
        default:
            return state;
    }
}
export function CreatePartnerModal() {
    const queryClient = useQueryClient();
    const [, setActiveChannel] = useAtom(activeChannelAtom);

    const { openPartnerCreateModal, setOpenPartnerCreateModal } =
        useCreatePartnerModal();
    const [domainInput, setDomainInput] = useState('');
    const [partnerDomains, dispatch] = useReducer(partnerDomainsReducer, []);
    const [previousPartnerDomains, setPreviousPartnerDomains] = useState<
        string[]
    >([]);
    const [domainError, setDomainError] = useState<string | null>(null);
    const activePath = usePathname();

    const campaignId = activePath?.split('/')[5] || null;

    const { channel, isChannelLoading, refetchChannel } =
        useChannel(campaignId);

    useEffect(() => {
        if (openPartnerCreateModal && channel?.partners && !isChannelLoading) {
            setPreviousPartnerDomains(
                channel.partners
                    .filter((partner) => partner.domain !== null)
                    .filter((partner) => !partner.isArchived)
                    .map((partner) => partner.domain as string)
            );
            dispatch({
                type: 'SET_DOMAINS',
                domains: channel.partners
                    .filter((partner) => partner.domain !== null)
                    .filter((partner) => !partner.isArchived)
                    .map((partner) => partner.domain as string),
            });
        }
    }, [openPartnerCreateModal, channel, isChannelLoading]);

    useEffect(() => {
        if (!openPartnerCreateModal) {
            dispatch({ type: 'RESET' });
            setDomainInput('');
        }
    }, [openPartnerCreateModal]);

    const mutation = useMutation({
        mutationFn: async (data: {
            campaignId: string;
            partnerDomains: string[];
        }) => {
            if (!data.campaignId) throw new Error('No campaign ID');
            const response = await createPartners(
                data.partnerDomains,
                data.campaignId
            );
            if ('error' in response) throw new Error(response.error);
            return response.data;
        },
        onSuccess: async () => {
            dispatch({ type: 'RESET' });
            setDomainInput('');
            setDomainError(null);

            if (campaignId) {
                await queryClient.invalidateQueries({
                    queryKey: ['channel', campaignId],
                    exact: true,
                });

                // Refetch the channel to get updated data and update the atom
                const updatedChannelData = await refetchChannel();
                if (updatedChannelData.data) {
                    setActiveChannel(updatedChannelData.data);
                }
            }
            toast.success('Collaboration partners updated successfully');
            setOpenPartnerCreateModal(false);
        },
    });
    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!campaignId || !partnerDomains.length) return;

        if (
            partnerDomains.length === previousPartnerDomains.length &&
            partnerDomains.every((domain) =>
                previousPartnerDomains.includes(domain)
            )
        ) {
            return;
        }
        mutation.mutate({ campaignId, partnerDomains });
    };
    const handleAddDomain = () => {
        if (domainInput.trim()) {
            let processedDomain = domainInput.trim();

            // Automatically append .com if not present
            if (!processedDomain.toLowerCase().endsWith('.com')) {
                processedDomain = `${processedDomain}.com`;
            }

            if (!partnerDomains.includes(processedDomain)) {
                dispatch({ type: 'ADD_DOMAIN', domain: processedDomain });
                setDomainInput('');
            }
        }
    };
    const handleDeleteDomain = (domain: string) => {
        dispatch({ type: 'DELETE_DOMAIN', domain });
    };

    const partnerForm = useForm<z.infer<typeof partnerDomainSchema>>({
        resolver: zodResolver(partnerDomainSchema),
        defaultValues: {
            partnerDomains: [],
        },
    });

    return (
        <Dialog
            open={openPartnerCreateModal}
            onOpenChange={setOpenPartnerCreateModal}
        >
            <DialogContent className="h-[450px] flex flex-col">
                <DialogHeader className="shrink-0">
                    <DialogTitle>
                        {workspaceModalSteps.invitePartnersTitle}
                    </DialogTitle>
                </DialogHeader>
                <div key={'invites'} className="flex flex-col h-full">
                    <Form {...partnerForm}>
                        <form
                            onSubmit={onSubmit}
                            className="flex flex-col h-full justify-start space-y-6"
                            autoComplete="off"
                        >
                            <div className="space-y-6 flex-1">
                                <FormItem className="flex flex-col">
                                    <FormDescription>
                                        {
                                            workspaceModalSteps.invitePartnersDescription
                                        }
                                    </FormDescription>
                                    <FormControl>
                                        <div className="flex space-x-2">
                                            <Input
                                                placeholder="domain.com"
                                                value={domainInput}
                                                onChange={(e) =>
                                                    setDomainInput(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <Button
                                                type="button"
                                                onClick={handleAddDomain}
                                            >
                                                Add
                                            </Button>
                                        </div>
                                    </FormControl>

                                    {domainError && (
                                        <FormMessage>{domainError}</FormMessage>
                                    )}
                                </FormItem>
                                <div className="flex-1 min-h-0">
                                    {' '}
                                    {/* Add this wrapper */}
                                    <div className="h-full overflow-y-auto">
                                        <ul className="flex flex-col gap-2">
                                            {partnerDomains
                                                .filter(
                                                    (domain) =>
                                                        !domain.includes(
                                                            channel?.domain ||
                                                                ''
                                                        )
                                                )

                                                .map((domain, index) => (
                                                    <li
                                                        key={index}
                                                        className="flex justify-between items-center"
                                                    >
                                                        <span>{domain}</span>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() =>
                                                                handleDeleteDomain(
                                                                    domain
                                                                )
                                                            }
                                                        >
                                                            Delete
                                                        </Button>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-2 mt-auto pt-6">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() =>
                                        setOpenPartnerCreateModal(false)
                                    }
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1"
                                    // disabled={workspaceSubmit.isPending}
                                >
                                    Add Partners
                                    {/* {workspaceSubmit.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
                                            Creating...
                                        </>
                                    ) : (
                                        'Finish'
                                    )} */}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </DialogContent>
        </Dialog>
    );
}
